import React, { useEffect, useMemo, useState } from 'react'
import { SearchMd, FilterLines, Stars01 } from '@untitled-ui/icons-react'
import {
  MyButton,
  MyDataTable,
  MyColumn,
  MyDoubleCard,
} from '@interstellar-component'
import { useWorkforce } from '../../../Context'
import RiskAssessmentTable from '../../EmployeeDetailsSheet/Tabs/ReportsContent/components/Sidebar/RiskAssessmentTable'
import { levelToBadge } from '../../EmployeeDetailsSheet/adapters/workforceDetailAdapter'

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(String(value).replace(/\//g, '-'))
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const PAGE_SIZE = 10

export default function EmploymentRecordsTab() {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const { workforceDetailRiskSignals } = useWorkforce()

  const section = workforceDetailRiskSignals?.employment_records
  const keyTakeaway = section?.overview?.key_takeaway
  const riskIndicators = section?.overview?.risk_indicators
  const recordCount = section?.records?.length ?? section?.count ?? 0

  // Employment consistency (1–10) and Overall risk (low/medium/high) are AI-
  // computed in core/service/credit-report/ai_prompt.js; rationales feed the
  // tooltip. Records found is deterministic from CLIK employmentHistory length.
  const consistencyScore = riskIndicators?.employment_consistency?.score
  const overallRiskBadge = levelToBadge(riskIndicators?.overall_risk?.level)

  const indicators = [
    {
      label: 'Employment consistency',
      value: consistencyScore != null ? `${consistencyScore} / 10` : undefined,
      tooltip: riskIndicators?.employment_consistency?.rationale,
    },
    { label: 'Employment records found', value: String(recordCount) },
    {
      label: 'Overall risk',
      badge: riskIndicators?.overall_risk?.level ? overallRiskBadge.badge : undefined,
      badgeColor: overallRiskBadge.badgeColor,
      tooltip: riskIndicators?.overall_risk?.rationale,
    },
  ]

  // CLIK has no per-record count concept; column kept for visual parity with
  // the Address Records tab and rendered as "—".
  const allRows = useMemo(() => {
    const records = section?.records ?? []
    return records.map((r) => ({
      occupation: r.occupation || '—',
      subtext: r.workplace || '—',
      industry: r.sector || '—',
      location: r.workplace_address || '—',
      count: '—',
      lastUpdate: formatDate(r.last_update_date),
    }))
  }, [section?.records])

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return allRows
    return allRows.filter((row) => (
      row.occupation.toLowerCase().includes(q)
      || row.subtext.toLowerCase().includes(q)
      || row.industry.toLowerCase().includes(q)
      || row.location.toLowerCase().includes(q)
    ))
  }, [allRows, searchTerm])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))

  // Snap currentPage back into range when filtering shrinks the result set
  // (e.g. user is on page 3, types a query that matches 5 rows → reset to 1).
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1)
  }, [currentPage, totalPages])

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredRows.slice(start, start + PAGE_SIZE)
  }, [filteredRows, currentPage])

  const employmentData = pagedRows.length ? pagedRows : null

  return (
    <div className="grid lg:grid-cols-12 gap-8 pt-4">
      {/* Sidebar */}
      <div className="lg:col-span-4 flex flex-col gap-8 h-full lg:border-r lg:border-gray-200 lg:pr-8">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            Overview
            <Stars01 className="h-5 w-5 text-brand/900" />
          </h3>
          <p className="text-sm-regular text-gray-600">
            Key insights from credit and background data.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <MyDoubleCard heading="Key takeaway">
            <p className="text-sm-regular text-gray-600 leading-relaxed">
              {keyTakeaway || 'No employment insights available yet.'}
            </p>
          </MyDoubleCard>

          <RiskAssessmentTable data={indicators} />
        </div>
      </div>

      {/* Main Table Content */}
      <div className="lg:col-span-8 flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900">Employment records</h3>
          <p className="text-sm-regular text-gray-600">
            Employment records associated with the individual reporting sources.
          </p>
        </div>

        <div className="flex flex-col gap-0 border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
          {/* Table Controls */}
          <div className="flex p-4 items-center justify-between border-b border-gray-200 gap-4">
            <div className="relative flex-1 max-w-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchMd className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-white p-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-brand/500 focus:outline-none focus:ring-1 focus:ring-brand/500"
                placeholder="Search for employment"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>

            <div className="flex items-center gap-3">
              <MyButton color="secondary" variant="outlined" size="md">
                <FilterLines className="h-4 w-4" />
                Filters
              </MyButton>
            </div>
          </div>

          {/* Table */}
          {employmentData && employmentData.length > 0 ? (
            <MyDataTable values={{ data: employmentData }} className="border-none shadow-none">
              <MyColumn
                header="Occupation"
                field="occupation"
                body={(row) => (
                  <div className="flex flex-col gap-0.5 py-1">
                    <span className="text-sm-semibold text-gray-900">{row.occupation}</span>
                    <span className="text-xs text-gray-500 font-normal">{row.subtext}</span>
                  </div>
                )}
              />
              <MyColumn
                header="Industry"
                field="industry"
                body={(row) => (
                  <span className="text-sm-regular text-gray-600 block py-1">{row.industry}</span>
                )}
              />
              <MyColumn
                header="Workplace"
                field="location"
                body={(row) => (
                  <span className="text-sm-regular text-gray-600 block py-1">{row.location}</span>
                )}
              />
              <MyColumn
                header="Count"
                field="count"
                body={(row) => (
                  <span className="text-sm-regular text-gray-600 block py-1">{row.count}</span>
                )}
              />
              <MyColumn
                header="Last Update Date"
                field="lastUpdate"
                body={(row) => (
                  <span className="text-sm-regular text-gray-600 block py-1">{row.lastUpdate}</span>
                )}
              />
            </MyDataTable>
          ) : (
            <div className="px-6 py-10 text-center text-sm-regular text-gray-500">
              No employment records linked to this individual.
            </div>
          )}

          {/* Footer / Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm-regular text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <MyButton
                color="secondary"
                variant="outlined"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </MyButton>
              <MyButton
                color="secondary"
                variant="outlined"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </MyButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
